/**
 * Hook da posicao aberta CCEE (Task #50 Fase C).
 *
 * Mesmo padrao do useCurvaForward: cache + refetch periodico (TTL 10min —
 * posicao muda quando Mesa atualiza curva forward).
 *
 * Task #206: aceita `mesRef` ("YYYY-MM-DD", dia 1 do fornecimento) pra
 * filtrar a posicao do mes navegado no Card. Antes o hook nao recebia data
 * e sempre batia no default do endpoint (proximo 6du oficial) — o KPI
 * "Posicao aberta" congelava no mesmo valor ao navegar prev/next.
 *   - `mesRef = string`  -> fetch filtrado por aquele mes_ref.
 *   - `mesRef = undefined` -> default (proximo 6du oficial; legado).
 *   - `mesRef = null`    -> 6du historico: nao fetcha, devolve vazio pro
 *                           Card mostrar "—" (foco do #206 e o futuro).
 * Cache chaveado por mesRef ("default" quando undefined) pra nao vazar o
 * resultado de um mes na navegacao pra outro (bug de cache do spike).
 *
 * Em caso de erro de rede/auth/5xx -> array vazio + flag de erro. UI
 * consumidora decide se mostra empty state ou aviso.
 */
import { useEffect, useState } from "react";
import { getPosicaoAberta } from "../api/client";
import type { PosicaoAbertaResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PosicaoAbertaResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePosicaoAbertaCache(): void {
  cache.clear();
}

const EMPTY: PosicaoAbertaResponse = {
  linhas_posicao_aberta: [],
  total_estimado: "0",
  total_volume_mwh: "0",
  total_volume_mwm: "0",
  total_valor_mtm_estimado: "0",
  linhas_sem_mtm: 0,
  mes_ref: null,
  dt_proximo_6du: null,
};

export interface UsePosicaoAbertaState {
  data: PosicaoAbertaResponse;
  isLoading: boolean;
  error: Error | null;
}

export function usePosicaoAberta(mesRef?: string | null): UsePosicaoAbertaState {
  // 6du historico (mesRef === null): nao fetcha — Card mostra "—". Estado
  // inicial reflete isso pra nao piscar "carregando" antes do efeito rodar.
  const skip = mesRef === null;
  const [state, setState] = useState<UsePosicaoAbertaState>({
    data: EMPTY,
    isLoading: !skip,
    error: null,
  });

  useEffect(() => {
    if (mesRef === null) {
      setState({ data: EMPTY, isLoading: false, error: null });
      return;
    }

    let cancel = false;
    // mesRef undefined -> "default" (proximo 6du oficial, comportamento legado).
    const key = mesRef ?? "default";

    function load() {
      const now = Date.now();
      const entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        cache.set(key, {
          expiresAt: now + REFETCH_MS,
          promise: mesRef
            ? getPosicaoAberta({ mes_ref_inicial: mesRef, mes_ref_final: mesRef })
            : getPosicaoAberta(),
        });
      }
      cache
        .get(key)!
        .promise.then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          setState({ data: EMPTY, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [mesRef]);

  return state;
}
